/**
 * net/labels.js — who a goose is, and how you can tell at a glance.
 *
 * Two concerns that belong together: the goose-pun name pool (used for NPCs,
 * for the local player's default handle, and as the fallback when a remote peer
 * sends a hostile name), and the billboarded canvas sprites that float those
 * names — plus the occasional HONK! — above a goose's head.
 *
 * Sprites are used rather than planes deliberately: a THREE.Sprite always faces
 * the camera, which is exactly what a name tag must do, and costs nothing to
 * keep correct in VR where there are two cameras with different orientations.
 *
 * @module net/labels
 */

import * as THREE from 'three';
import { NPC } from '../constants.js';

/**
 * The pun pool. Deliberately larger than `NPC.count` so a re-seed visibly
 * changes the cast, and so remote players who never set a name still read as
 * individuals rather than four identical Anonymous Geese.
 */
export const GOOSE_NAMES = Object.freeze([
  'Goosifer', 'Gustav', 'Honk Solo', 'Beaktrix', 'Quackie Chan', 'Waddlesworth',
  'Sir Honksalot', 'Gooserella', 'Egg Sheeran', 'Featherstone', 'Honkleberry',
  'Amelia Egghart', 'Goosezilla', 'Bill Murray', 'Plucky', 'Nibbles McGraw',
  'Duchess Waddle', 'Captain Beakbeard', 'Honk Ferguson', 'Gandergalf',
]);

/**
 * Pick `n` distinct names using a supplied generator.
 *
 * The generator is injected rather than imported so callers can pass a private
 * per-goose stream and stay deterministic regardless of what else drew from the
 * global RNG first.
 *
 * @param {number} n
 * @param {() => number} rand Returns [0, 1).
 * @returns {string[]}
 */
export function pickNames(n, rand) {
  const pool = [...GOOSE_NAMES];
  const out = [];
  for (let i = 0; i < n && pool.length > 0; i++) {
    out.push(pool.splice(Math.floor(rand() * pool.length), 1)[0]);
  }
  return out;
}

/**
 * A stable pun name derived from an arbitrary string — used to give a remote
 * peer a readable identity when it never sent one.
 * @param {string} seedText
 * @returns {string}
 */
export function nameFromId(seedText = '') {
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i++) {
    h ^= seedText.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return GOOSE_NAMES[(h >>> 0) % GOOSE_NAMES.length];
}

const NAME_KEY = 'carnival-chaos-goose-name';

/**
 * This browser's own display name, drawn once and remembered.
 *
 * Kept in its own localStorage key rather than the save document: the save
 * schema is owned by core and versioned, and a nickname is not worth a
 * migration. A browser with storage blocked just gets 'Goose'.
 *
 * The one deliberate `Math.random()` in this module. Determinism forbids it in
 * *gameplay*, and the seeded stream is exactly what must not be used here: a
 * name is drawn once per browser and then persisted forever, so taking a number
 * from the shared generator would shift every later draw — moving stall targets
 * and NPC plans — on first run only. This value is never asserted by a test.
 *
 * @returns {string}
 */
export function localGooseName() {
  try {
    const stored = localStorage.getItem(NAME_KEY);
    if (stored) return stored;
    const name = nameFromId(`${Date.now()}-${Math.random()}`);
    localStorage.setItem(NAME_KEY, name);
    return name;
  } catch {
    return 'Goose';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Canvas sprites
// ─────────────────────────────────────────────────────────────────────────────

const TAG_W = 320;
const TAG_H = 80;

/**
 * Draw text into a fresh canvas sized for a floating label.
 * @param {string} text
 * @param {{ bg?:string, color?:string, border?:string, size?:number }} [opts]
 * @returns {HTMLCanvasElement}
 */
function drawLabel(text, opts = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = TAG_W;
  canvas.height = TAG_H;
  const ctx = canvas.getContext('2d');

  const r = 18;
  ctx.clearRect(0, 0, TAG_W, TAG_H);
  ctx.fillStyle = opts.bg ?? 'rgba(18, 22, 38, 0.82)';
  ctx.beginPath();
  ctx.moveTo(r, 4);
  ctx.arcTo(TAG_W - 4, 4, TAG_W - 4, TAG_H - 4, r);
  ctx.arcTo(TAG_W - 4, TAG_H - 4, 4, TAG_H - 4, r);
  ctx.arcTo(4, TAG_H - 4, 4, 4, r);
  ctx.arcTo(4, 4, TAG_W - 4, 4, r);
  ctx.closePath();
  ctx.fill();

  if (opts.border) {
    ctx.strokeStyle = opts.border;
    ctx.lineWidth = 4;
    ctx.stroke();
  }

  ctx.font = `bold ${opts.size ?? 40}px "Trebuchet MS", sans-serif`;
  ctx.fillStyle = opts.color ?? '#ffd166';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(text), TAG_W / 2, TAG_H / 2 + 2);

  return canvas;
}

/**
 * A billboarded name tag.
 *
 * `depthTest` stays on so a tag behind a booth is correctly occluded — without
 * it every goose on the midway shows its name through the canvas walls, which
 * looks like a bug rather than a feature.
 *
 * @param {string} text
 * @param {{ color?:string, border?:string, scale?:number }} [opts]
 * @returns {THREE.Sprite} with `userData.setText(t)`.
 */
export function createNameTag(text, opts = {}) {
  const canvas = drawLabel(text, opts);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: true,
    depthWrite: false,
  }));
  const s = opts.scale ?? 1;
  sprite.scale.set(0.86 * s, 0.215 * s, 1);
  sprite.position.y = NPC.nameTagHeight;
  sprite.name = 'name-tag';
  sprite.renderOrder = 5;

  sprite.userData.setText = (t) => {
    const next = drawLabel(t, opts);
    texture.image = next;
    texture.needsUpdate = true;
  };
  sprite.userData.dispose = () => {
    texture.dispose();
    sprite.material.dispose();
  };
  return sprite;
}

/**
 * A short-lived speech bubble — HONK!, mostly.
 * Starts hidden; `show(text, seconds)` pops it and `update(dt)` retires it.
 *
 * @returns {THREE.Sprite} with `userData.show(text, seconds)` and
 *   `userData.update(dt)`.
 */
export function createSpeechBubble() {
  const sprite = createNameTag('HONK!', {
    bg: 'rgba(255, 209, 102, 0.94)',
    color: '#1b2340',
    border: 'rgba(27, 35, 64, 0.6)',
    scale: 0.92,
  });
  sprite.name = 'speech-bubble';
  sprite.position.y = NPC.nameTagHeight + 0.3;
  sprite.visible = false;

  let remaining = 0;
  sprite.userData.show = (text, seconds = NPC.bubbleSeconds) => {
    sprite.userData.setText(text);
    sprite.visible = true;
    remaining = seconds;
  };
  sprite.userData.update = (dt) => {
    if (!sprite.visible) return;
    remaining -= dt;
    // A small rise as it fades reads as "said out loud" rather than "popped up".
    sprite.position.y = NPC.nameTagHeight + 0.3 + (1 - Math.max(remaining, 0)) * 0.06;
    if (remaining <= 0) {
      sprite.visible = false;
      sprite.position.y = NPC.nameTagHeight + 0.3;
    }
  };
  return sprite;
}
